import React from "react";
import { Lead, Section, P, Bullets, Callout, CTA } from "../../../../blog/components";

export const title = "AI 编程 Agent 的界面难题";
export const excerpt =
  "终端当初是为「一个人、一次敲一条命令」设计的。AI Agent 却把它变成了一堆半自主进程同时开工的地方，而我们的界面范式还没跟上。";
export const heroAlt = "莫比乌斯风格的电影感风景，黄金时刻的光线";
export const readingTime = "6 分钟阅读";

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#5ba8f5", textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "OpenAI，《Codex CLI》，OpenAI 开发者文档。文档将 Codex CLI 描述为一个本地终端编程 Agent，可以在所选目录里读取、修改并运行代码。",
    href: "https://developers.openai.com/codex/cli",
    label: "developers.openai.com/codex/cli",
  },
  {
    text: "Anthropic，《Agent SDK 概述》，Claude Code 文档。Agent SDK 用来构建能读取文件、运行命令、联网搜索、编辑代码的 Agent，并复用驱动 Claude Code 的那套工具、Agent 循环和上下文管理。",
    href: "https://code.claude.com/docs/en/agent-sdk/overview",
    label: "code.claude.com/docs/en/agent-sdk/overview",
  },
  {
    text: "GitHub，《关于 Copilot 编程 Agent》，GitHub 文档。Copilot 编程 Agent 能调研代码库、规划实现、在分支上改代码、让开发者审查 diff，并发起 pull request。",
    href: "https://docs.github.com/copilot/concepts/agents/coding-agent/about-coding-agent",
    label: "docs.github.com/copilot/…/about-coding-agent",
  },
  {
    text: "Git，《git-worktree 文档》，Git 官方文档。git worktree 让一个代码库支持多个工作树，允许同时检出多个 branch。",
    href: "https://git-scm.com/docs/git-worktree",
    label: "git-scm.com/docs/git-worktree",
  },
  {
    text: "Anthropic，《工具使用概述》，Claude API 文档。工具使用被设计成一个 Agent 循环：由 Claude 决定何时调用某个工具，再由应用或 Anthropic 根据工具类型去执行。",
    href: "https://platform.claude.com/docs/en/agents-and-tools/tool-use/overview",
    label: "platform.claude.com/docs/…/tool-use/overview",
  },
];

export default function Post() {
  return (
    <>
      <Lead>很长一段时间里，终端都是开发者工作流中最简单的一环。</Lead>

      <P>
        开个 shell，跑几条命令，看看输出，切个目录，也许再分个面板，然后继续干活。它是一块用来直接执行的、轻薄又快速的画面，谈不上是完整的工作区。
      </P>
      <P>
        AI 编程 Agent 把这一切改变了。它们会写代码。更要紧的是，它们如今就泡在开发者做最多有状态工作的那个环境里：跑测试、看日志、切 branch、起服务、查文件、调 bug。
      </P>
      <P>
        于是终端成了它们天然的落脚点。OpenAI 的 Codex CLI 在终端本地运行，能读取、修改并运行某个目录里的代码。<Fn n={1} /> Anthropic 的 Claude
        Agent SDK 所构建的 Agent，靠的正是驱动 Claude Code 的那套 Agent 循环和上下文管理来读文件、跑命令、改代码。<Fn n={2} /> GitHub 的 Copilot 编程 Agent 则会调研代码库、做规划、在 branch 上改代码，最后发起 pull request。<Fn n={3} />
      </P>
      <P>
        所以终端不再只是你敲命令的地方。它正在变成这样一处场所：好几个半自主进程在里面行动、暂停、请求批准、改动文件，然后等着你重新介入。可我们的界面范式，绝大多数是为「人驱动的命令执行」而生的，而不是为「盯着一群长时间运行的 Agent」而生的。
      </P>

      <Section title="旧模式：一条命令，一个结果">
        <P>
          传统终端的使用方式是线性的。你敲一条命令，机器回一段结果，你读一读，再决定下一条敲什么。标签页、面板、多路复用器，给的只是更多「跑命令的地方」，并没有给你一种理解进行中工作的新方式。
        </P>
        <P>
          Agent 会话打破了这个模式。它不只是输出，而是一项带着意图、上下文、状态、权限和副作用的任务，更像一个活的工作对象，而不是一段终端缓冲区。可我们还是把它当成标签页里的一堆文本。
        </P>
      </Section>

      <Section title="新模式：多任务，多上下文">
        <P>
          一个 Agent 一旦用顺手，你立刻就想要好几个：一个在修测试，一个在试重构，一个在审 pull request，还有一个在另一条 branch 上跑实验。
        </P>
        <P>
          这正是 Git worktree 的价值所在。它让一个代码库挂着多个工作树，同时检出多个 branch。<Fn n={4} /> Agent 是在文件系统里直接动手的，一旦它们共用同一份检出、同一个服务、同一个端口或同一个数据库，结果就会含糊不清，最后还得靠人去复盘到底发生了什么。哪怕界面没把它呈现成这样，实际的工作流也已经变成空间化的了。你会不自觉地按「区域」来思考：
        </P>
        <Bullets
          items={[
            "一条 branch、一个服务、一个跑挂的测试。",
            "一个 Agent 走保守修法，另一个走激进修法。",
            "一个预览、一条日志流、一份 diff。",
          ]}
        />
        <P>这已经不是终端的问题了，是工作区的问题。</P>
      </Section>

      <Section title="标签页留不住意图">
        <P>
          标签页只能告诉你「这里有个终端」，却几乎说不清它为什么在那儿：属于哪项任务、在哪条 branch 上、动没动过文件、能不能放心关掉，或者里面的 Agent 是不是正等着你。对一个 shell 来说这无所谓。可当终端里装的是一个已经推理了十分钟、改了六个文件、此刻正请求执行一次高风险迁移的 Agent 时，这就远远不够了。
        </P>
        <P>一个真正好用的 Agent 工作区，应该一眼就能回答：</P>
        <Bullets
          items={[
            "这个 Agent 在干什么，基于什么状态？",
            "它改了什么，又已经试过哪些路子？",
            "它是被卡住、在等待、已失败，还是还在干？",
            "我能不能对比两次尝试，明天接着干？",
          ]}
        />
        <P>
          而且 Agent 的输出并不是日志。里头夹着决策、假设、计划，还有权限边界。构建日志可以扫一眼带过；Agent 的对话记录却得逐句去读懂。像 Codex CLI、Claude Code 这类终端原生 Agent 紧贴真实环境，<Fn n={1} /><Fn n={2} /> 这让它们很强，但也意味着界面要传达的早就不止是文本。
        </P>
      </Section>

      <Section title="难的是盯，不是开">
        <Callout>
          开一个 Agent 很容易，盯一个就难一些，同时盯好几个则难得多。
        </Callout>
        <P>
          一个贴着文件树的聊天框，应付小任务够用。可一旦活儿进入「运维」状态，要用到终端、diff、预览、测试结果、日志和 branch，它就吃力了。每种形态都有自己的取舍：
        </P>
        <Bullets
          items={[
            "IDE 里的 Agent 贴近代码，却不一定贴近运行时。",
            "终端里的 Agent 贴近运行时，却不一定有条理。",
            "像 Copilot 这样的云端 pull request Agent 适合审查，不适合实时操控。",
          ]}
        />
        <P>
          Copilot 围绕基于 branch 的工作和一份可审查的 diff 来设计；<Fn n={3} /> Codex CLI 扎根在终端本地；<Fn n={1} /> Claude Agent SDK 则以编程方式把同一套文件、命令、代码编辑循环暴露出来。<Fn n={2} /> 它们谁都不是完整答案。所以问题其实很简单：当有用的工作同时发生在好几处时，什么样的界面能让开发者始终掌控全局？
        </P>
      </Section>

      <Section title="要暴露的是状态，而不只是数据流">
        <P>
          一个更好的工作区，会把每项任务当成一等对象，而不只是一个标签页、一段对话记录或一条 branch。这个对象随身带着自己的指令、工作目录、所在的 branch 或 worktree、跑过的命令、改过的文件、当前状态、批准记录，还有你的批注。
        </P>
        <P>
          这并不是要藏起终端。终端是诚实的：它把真实的命令画面摊给你看，随时可查。界面不该取代它，而该用记忆、结构和导航把它包起来，就像开发者早就习惯把窗口摆来摆去、拿空间当外部记忆一样。
        </P>
        <P>
          安全也是这套逻辑的一部分。如果你不知道是哪项任务发的请求、它动过什么、它接下来要改什么，那句「允许执行此命令？」根本拦不住事。工具越有 Agent 属性，界面就越得把来龙去脉摊开：谁发起的、依据哪条指令、在什么状态上、哪些能回滚。Anthropic 把这个 Agent 循环描述为：模型决定调用某个工具，再由应用或 Anthropic 去执行；<Fn n={5} /> 而一个好的界面，会让这些边界一目了然。
        </P>
      </Section>

      <Section title="终端能赢，靠的是可组合">
        <P>
          人很容易动一个念头：干脆造一个庞大的、IDE 形态的命令中枢。这多半是错的直觉。终端之所以这么多年不倒，恰恰因为它可组合：它能配合任何语言、框架、包管理器，乃至一段半残的本地脚本，因为它本身知道得很少，只是让别的工具借它发声。所以 Agent 工作区与其做成一个「AI IDE」，不如做成一块协调画面：
        </P>
        <Bullets
          items={[
            "终端、branch、diff 和日志，都保持原样真实。",
            "Agent 作为带状态的「工人」被看得见。",
            "人，始终是那个拍板「什么重要」的人。",
          ]}
        />
      </Section>

      <Section title="真正的瓶颈是注意力">
        <P>
          这么多年来，开发者工具一直在为打字速度和自动补全做优化。有了 Agent，瓶颈从「按键」挪到了「注意力」：我能不能看懂正在发生什么、敢不敢信这次改动、能不能对比两次尝试、明天能不能接着干、关进程时关的是不是对的那个，以及，分得清一个 Agent 是卡住了还是真在干活？
        </P>
        <P>
          下一层真正有用的东西，多半不是更聪明的聊天框，而是一个工作区模型：它把任务看得比消息重、把 worktree 看得比文件夹重、把 Agent 状态看得比输出重、把批准看得比提示重、把历史看得比回滚缓冲重。终端依然会在，甚至比以往更重要。可一旦工作的最小单位大过「一条命令」，它就未必够用了。
        </P>
        <P>旧的那些画面，正开始把最重要的东西遮住：</P>
        <p style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
          到底在发生什么？
        </p>
      </Section>

      <Callout>
        CATE 就是我们对这块协调画面的回答：把终端、编辑器、浏览器和 Agent，统统作为一等窗口，摆在同一块无限画布上。
      </Callout>
      <CTA label="认识 CATE" href="/" />

      <Section title="参考文献">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: "#5ba8f5", wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
